import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AgentPreviewModal from '../components/AgentPreviewModal';
import OpportunityCard from '../components/opportunities/OpportunityCard';
import UpgradeModal from '../components/UpgradeModal';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { usePlan } from '../context/PlanContext';
import { fetchWithOrg } from '../utils/api';

import {
  ArrowRight,
  CheckCircle,
  CircleNotch,
  Clock,
  CurrencyDollar,
  Funnel,
  Lightning,
  Lock,
  MagnifyingGlass,
  Pulse,
  Robot,
  Sparkle,
  Target,
  TrendUp,
  Warning,
  WarningCircle,
  X,
  XCircle
} from '@phosphor-icons/react';
const OpportunitiesDashboard = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { userPlan, isFreePlan } = usePlan();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [opportunities, setOpportunities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  
  // URL-based filters
  const [urlFilter, setUrlFilter] = useState({
    source: searchParams.get('source'),
    jd_id: searchParams.get('jd_id'),
    jd_title: searchParams.get('jd_title')
  });
  
  // Blueprint unlocked state
  const [showUnlockedBanner, setShowUnlockedBanner] = useState(false);
  const [highlightOpportunityId, setHighlightOpportunityId] = useState(null);
  
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Check if user is on Basic plan (not Pro or Partner Pro)
  // Pro tier includes: 'pro', 'partner_pro', 'partner-pro', 'enterprise', etc.
  const isProOrHigher = userPlan === 'pro' || userPlan === 'partner_pro' || userPlan === 'partner-pro' || userPlan === 'enterprise';
  const isPartnerPro = userPlan === 'partner_pro' || userPlan === 'partner-pro' || userPlan === 'enterprise';
  const isBasicPlan = !isProOrHigher && (isFreePlan() || !userPlan || userPlan === 'basic' || userPlan === 'starter');
  // Non-Partner Pro tiers: Basic, Professional, Teams — limited opportunities view
  const isLimitedTier = !isPartnerPro;

  // Handle blueprint unlocked state from URL params
  useEffect(() => {
    const unlocked = searchParams.get('unlocked');
    const highlight = searchParams.get('highlight');
    const blueprintClaimed = sessionStorage.getItem('blueprint_claimed');
    
    if (unlocked === 'true' || blueprintClaimed === 'true') {
      setShowUnlockedBanner(true);
      // Clear the flag after showing
      sessionStorage.removeItem('blueprint_claimed');
      
      // Auto-dismiss banner after 8 seconds
      const timer = setTimeout(() => setShowUnlockedBanner(false), 8000);
      return () => clearTimeout(timer);
    }
    
    if (highlight) {
      setHighlightOpportunityId(highlight);
      sessionStorage.removeItem('highlight_opportunity_id');
    }
  }, [searchParams]);

  // Handle toggling favorite status with optimistic update
  const handleToggleFavorite = async (opportunityId, newFavoriteStatus) => {
    // Optimistic update - update local state immediately
    setOpportunities(prev => 
      prev.map(opp => 
        (opp.id || opp.opportunity_id) === opportunityId 
          ? { ...opp, is_favorite: newFavoriteStatus }
          : opp
      )
    );

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/opportunities/${opportunityId}/favorite?organization_id=${activeOrganization?.organization_id}`,
        {
          method: 'PATCH',
          credentials: 'include'
        },
        activeOrganization?.organization_id
      );

      if (!response.ok) {
        // Revert on failure
        setOpportunities(prev => 
          prev.map(opp => 
            (opp.id || opp.opportunity_id) === opportunityId 
              ? { ...opp, is_favorite: !newFavoriteStatus }
              : opp
          )
        );
        console.error('Failed to toggle favorite');
      }
    } catch (err) {
      // Revert on error
      setOpportunities(prev => 
        prev.map(opp => 
          (opp.id || opp.opportunity_id) === opportunityId 
            ? { ...opp, is_favorite: !newFavoriteStatus }
            : opp
        )
      );
      console.error('Error toggling favorite:', err);
    }
  };

  const categories = [
    { value: 'all', label: 'All Categories' },
    // Source-based categories (from different entry points)
    { value: 'process', label: 'Process Automation' },  // From process definitions
    { value: 'role', label: 'Role-Based' },  // From org chart analysis
    // Functional categories
    { value: 'Customer Experience', label: 'Customer Experience' },
    { value: 'Sales Automation', label: 'Sales Automation' },
    { value: 'Marketing Automation', label: 'Marketing Automation' },
    { value: 'Finance Automation', label: 'Finance Automation' },
    { value: 'Operations', label: 'Operations' },
    { value: 'HR', label: 'HR' },
    { value: 'Legal/Compliance', label: 'Legal/Compliance' },
    { value: 'Productivity', label: 'Productivity' },
    { value: 'communication_automation', label: 'Communication' },
    { value: 'process_automation', label: 'Process (Legacy)' }  // Legacy value
  ];

  const priorities = [
    { value: 'all', label: 'All Priorities' },
    { value: 'high', label: 'High', color: '#00E5C0' },
    { value: 'medium', label: 'Medium', color: '#F59E0B' },
    { value: 'low', label: 'Low', color: '#14F195' }
  ];

  const statuses = [
    { value: 'all', label: 'All Statuses' },
    { value: 'identified', label: 'Identified' },
    { value: 'evaluating', label: 'Evaluating' },
    { value: 'planning', label: 'Planning' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'implemented', label: 'Implemented' }
  ];

  const fetchOpportunities = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedPriority !== 'all') params.append('priority', selectedPriority);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);

      // Use unified opportunities API
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/opportunities?organization_id=${activeOrganization?.organization_id}&${params}`,
        { credentials: 'include' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setOpportunities(data.opportunities || []);
        setSummary(data.summary || null);
      } else {
        throw new Error('Failed to fetch opportunities');
      }
    } catch (err) {
      console.error('Error fetching opportunities:', err);
      setError('Failed to load opportunities. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeOrganization, selectedCategory, selectedPriority, selectedStatus]);

  useEffect(() => {
    if (activeOrganization) {
      fetchOpportunities();
    }
  }, [activeOrganization, fetchOpportunities]);

  // Frontend filtering
  const filteredOpportunities = opportunities.filter(opp => {
    // For limited tiers (Basic/Professional/Teams): only show AI recommendations and onboarding wizard items
    if (isLimitedTier) {
      const source = (opp.source || '').toLowerCase();
      const isAIRecommendation = !source || source === 'ai_recommendation' || source === 'ai' || source === 'system';
      const isOnboarding = source === 'onboarding' || source === 'wizard' || source === 'onboarding_wizard';
      if (!isAIRecommendation && !isOnboarding) return false;
    }

    // URL-based filters (from Job Descriptions, etc.)
    if (urlFilter.source && opp.source !== urlFilter.source) {
      return false;
    }
    if (urlFilter.jd_id && opp.linked_job_description_id !== urlFilter.jd_id) {
      return false;
    }
    
    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        (opp.title || '').toLowerCase().includes(query) ||
        (opp.description || '').toLowerCase().includes(query) ||
        (opp.category || '').toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }
    
    return true;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-[#00E5C0]';
      case 'medium':
        return 'text-[#F59E0B]';
      case 'low':
        return 'text-[#14F195]';
      default:
        return 'text-[#A0AEC0]';
    }
  };

  const getPriorityBgColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-[rgba(236,72,153,0.1)] border-[rgba(236,72,153,0.3)]';
      case 'medium':
        return 'bg-[rgba(245,158,11,0.1)] border-[rgba(245,158,11,0.3)]';
      case 'low':
        return 'bg-[rgba(20,241,149,0.1)] border-[rgba(20,241,149,0.3)]';
      default:
        return 'bg-[rgba(160,174,192,0.1)] border-[rgba(160,174,192,0.3)]';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'identified':
        return <WarningCircle size={16} className="text-[#00E5C0]" />;
      case 'evaluating':
        return <Pulse size={16} className="text-[#F59E0B]" />;
      case 'planning':
        return <Clock size={16} className="text-[#00D4B0]" />;
      case 'in_progress':
        return <Lightning size={16} className="text-[#14F195]" />;
      case 'implemented':
        return <CheckCircle size={16} className="text-[#14F195]" />;
      default:
        return <WarningCircle size={16} className="text-[#A0AEC0]" />;
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading && !opportunities.length) {
    return (
        <div className="flex items-center justify-center py-20">
          <CircleNotch size={48} className="text-[#00E5C0] animate-spin" />
        </div>
      
    );
  }

  return (
      <>
      <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-1">
              <Target size={24} className="text-[#00E5C0]" />
              <h1 className="text-2xl font-bold text-white" data-testid="opportunities-title">Opportunities</h1>
            </div>
            <p className="text-sm text-[#B0B8C4]">
              Ways to improve and automate your business using AI workers
            </p>
            
            {/* Active URL Funnel Badge */}
            {(urlFilter.source || urlFilter.jd_id) && (
              <div className="mt-4 flex items-center gap-2">
                <div className="px-4 py-2 rounded-lg bg-[rgba(99,102,241,0.2)] border border-[rgba(99,102,241,0.4)] flex items-center gap-2">
                  <Funnel size={16} className="text-[#00E5C0]" />
                  <span className="text-white text-sm font-semibold">
                    Filtered by: {urlFilter.source === 'job_description' ? 'Job Description' : 'Source'}
                    {urlFilter.jd_title && ` - ${urlFilter.jd_title}`}
                  </span>
                  <button
                    onClick={() => {
                      setUrlFilter({ source: null, jd_id: null, jd_title: null });
                      setSearchParams({});
                    }}
                    className="ml-2 p-1 rounded hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                  >
                    <X size={14} className="text-white" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] flex items-center gap-3"
            >
              <WarningCircle size={20} className="text-[#EF4444]" />
              <p className="text-[#EF4444] text-sm">{error}</p>
            </motion.div>
          )}

          {/* Blueprint Unlocked Banner */}
          <AnimatePresence>
            {showUnlockedBanner && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-6 p-4 rounded-xl bg-gradient-to-r from-[rgba(16,185,129,0.15)] to-[rgba(99,102,241,0.15)] border border-[rgba(16,185,129,0.3)] flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#10B981] flex items-center justify-center">
                    <CheckCircle size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Your blueprint has been unlocked</p>
                    <p className="text-sm text-[#A0AEC0]">Your AI automation opportunities are ready to explore</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowUnlockedBanner(false)}
                  className="text-[#A0AEC0] hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Summary Stats */}
          {summary && (
            <div className="grid md:grid-cols-4 gap-4 mb-8">
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Target size={20} className="text-[#00E5C0]" />
                  <span className="text-[#A0AEC0] text-sm">Total Opportunities</span>
                </div>
                <p className="text-3xl font-bold text-white">{summary.total || 0}</p>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Warning size={20} className="text-[#00E5C0]" />
                  <span className="text-[#A0AEC0] text-sm">High Priority</span>
                </div>
                <p className="text-3xl font-bold text-[#00E5C0]">{summary.high_priority || 0}</p>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Pulse size={20} className="text-[#F59E0B]" />
                  <span className="text-[#A0AEC0] text-sm">Medium Priority</span>
                </div>
                <p className="text-3xl font-bold text-[#F59E0B]">{summary.medium_priority || 0}</p>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Sparkle size={20} className="text-[#14F195]" />
                  <span className="text-[#A0AEC0] text-sm">Low Priority</span>
                </div>
                <p className="text-3xl font-bold text-[#14F195]">{summary.low_priority || 0}</p>
              </div>
            </div>
          )}

          {/* ── Curated Templates — shown when no opportunities exist yet ── */}
          {!loading && filteredOpportunities.length === 0 && !searchQuery && selectedCategory === 'all' && selectedPriority === 'all' && selectedStatus === 'all' && (
            <div className="mb-8" data-testid="curated-templates">
              <div className="flex items-center gap-2 mb-4">
                <Lightning size={18} className="text-[#00E5C0]" />
                <h3 className="text-white font-semibold text-base">Get started with a template</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { title: 'Handle customer enquiries', desc: 'Auto-reply to inbound emails and route complex ones to your team', category: 'Customer Experience', cta: 'Set up worker' },
                  { title: 'Follow up missed leads', desc: 'Automatically reach out to leads that went cold after initial contact', category: 'Sales Automation', cta: 'Set up worker' },
                  { title: 'Send review requests', desc: 'Trigger review requests after completed jobs to build your reputation', category: 'Marketing Automation', cta: 'Set up worker' },
                ].map((tpl, i) => (
                  <div
                    key={i}
                    data-testid={`template-card-${i}`}
                    className="rounded-xl bg-[var(--surface-2)] border border-[var(--border-default)] p-5 hover:border-[rgba(0,229,192,0.3)] transition-colors cursor-pointer group"
                    onClick={() => navigate('/automations')}
                  >
                    <span className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider">{tpl.category}</span>
                    <h4 className="text-white font-semibold mt-2 mb-1 text-sm">{tpl.title}</h4>
                    <p className="text-[#B0B8C4] text-xs leading-relaxed mb-4">{tpl.desc}</p>
                    <span className="text-[#00E5C0] text-xs font-medium group-hover:underline">{tpl.cta} &rarr;</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── AI-Recommended — shown for all tiers ── */}
          {filteredOpportunities.length > 0 && (() => {
            const aiRecommended = [...filteredOpportunities]
              .filter(o => o.priority_score || o.impact)
              .sort((a, b) => (b.priority_score || b.impact || 0) - (a.priority_score || a.impact || 0))
              .slice(0, 5);
            if (aiRecommended.length === 0) return null;
            return (
              <div className="mb-8" data-testid="ai-recommended-section">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkle size={16} weight="fill" className="text-[#00E5C0]" />
                  <h3 className="text-white font-semibold text-sm">AI Recommended</h3>
                  <span className="text-[10px] text-[#7D8896] ml-1">Based on your business data</span>
                </div>
                <div className="grid md:grid-cols-5 gap-3">
                  {aiRecommended.map((opp) => {
                    const oppId = opp.id || opp.opportunity_id;
                    return (
                      <div
                        key={`ai-rec-${oppId}`}
                        data-testid={`ai-rec-${oppId}`}
                        className="rounded-xl bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.12)] p-4 hover:border-[rgba(0,229,192,0.3)] transition-colors cursor-pointer"
                        onClick={() => navigate(`/opportunities/${oppId}`)}
                      >
                        <div className="flex items-center gap-1.5 mb-2">
                          <Sparkle size={12} weight="fill" className="text-[#00E5C0]" />
                          <span className="text-[9px] font-semibold text-[#00E5C0] uppercase tracking-wider">AI Pick</span>
                        </div>
                        <h4 className="text-white text-xs font-semibold leading-snug line-clamp-2 mb-2">{opp.title}</h4>
                        <div className="flex items-center gap-2">
                          {opp.impact && <span className="text-[10px] text-[#B0B8C4]">Impact {opp.impact}/10</span>}
                          {opp.linked_agent_name && (
                            <span className="text-[10px] text-[#00E5C0] flex items-center gap-1">
                              <Robot size={10} /> Active
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── Upgrade Banner for non-Partner Pro tiers ── */}
          {isLimitedTier && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 px-6 py-5 rounded-xl border border-[rgba(0,229,192,0.15)] bg-[rgba(0,229,192,0.03)]"
              data-testid="upgrade-to-partner-pro-banner"
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[rgba(0,229,192,0.1)] flex items-center justify-center">
                    <TrendUp size={20} className="text-[#00E5C0]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Unlock full business analysis tools</p>
                    <p className="text-[#B0B8C4] text-xs mt-0.5">
                      Partner Pro includes assessments, process analysis, job descriptions, org charts and all generated opportunities.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/billing')}
                  className="px-5 py-2.5 rounded-lg bg-[#00E5C0] hover:bg-[#00D4B0] text-[#0B0F14] text-sm font-semibold transition-colors flex items-center gap-1.5 shrink-0"
                  data-testid="upgrade-to-partner-pro-btn"
                >
                  Upgrade to Partner Pro <ArrowRight size={14} />
                </button>
              </div>
            </motion.div>
          )}

          {/* Filters — Partner Pro only */}
          {isPartnerPro && (
          <div className="glass rounded-2xl p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Funnel size={20} className="text-[#00E5C0]" />
              <h3 className="text-white font-bold">Filters</h3>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              {/* MagnifyingGlass */}
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A0AEC0]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search opportunities..."
                  className="w-full pl-10 pr-10 py-2 rounded-xl glass text-white placeholder-[#A0AEC0] focus:outline-none focus:ring-2 focus:ring-[#00E5C0]"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#A0AEC0] hover:text-white"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Category Funnel */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] [&>option]:bg-[#1a1b1e] [&>option]:text-white"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>

              {/* Priority Funnel */}
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="px-4 py-2 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] [&>option]:bg-[#1a1b1e] [&>option]:text-white"
              >
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>{priority.label}</option>
                ))}
              </select>

              {/* Status Funnel */}
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] [&>option]:bg-[#1a1b1e] [&>option]:text-white"
              >
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>

            {/* Active Filters Count */}
            {(searchQuery || selectedCategory !== 'all' || selectedPriority !== 'all' || selectedStatus !== 'all') && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-[#A0AEC0] text-sm">
                  {filteredOpportunities.length} opportunit{filteredOpportunities.length !== 1 ? 'ies' : 'y'} found
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                    setSelectedPriority('all');
                    setSelectedStatus('all');
                  }}
                  className="text-[#00E5C0] text-sm hover:text-[#00D4B0] transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
          )}

          {/* Opportunities List */}
          <div className="space-y-4">
            {/* Basic Plan Notice */}
            {isBasicPlan && filteredOpportunities.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 px-4 py-3 rounded-xl bg-[#00E5C0]/10 border border-[#00E5C0]/20 flex items-center gap-3"
              >
                <Lock size={16} className="text-[#00E5C0]" />
                <p className="text-sm text-[#A0AEC0]">
                  <span className="text-white font-medium">
                    {(() => {
                      // Count opportunities from processes beyond the first one
                      // Basic plan: Full access to FIRST process/blueprint only
                      const processGroups = {};
                      filteredOpportunities.forEach(opp => {
                        // Use process_id for blueprint_generator, scan_id for assessments
                        const groupKey = opp.process_id || opp.scan_id || 'default';
                        if (!processGroups[groupKey]) {
                          processGroups[groupKey] = { opps: [], created_at: opp.created_at };
                        }
                        processGroups[groupKey].opps.push(opp);
                        // Track earliest created_at for the group
                        if (opp.created_at && (!processGroups[groupKey].created_at || new Date(opp.created_at) < new Date(processGroups[groupKey].created_at))) {
                          processGroups[groupKey].created_at = opp.created_at;
                        }
                      });
                      
                      // Find oldest process (grant full access to it)
                      const sortedGroups = Object.entries(processGroups).sort((a, b) => {
                        const dateA = a[1].created_at ? new Date(a[1].created_at) : new Date();
                        const dateB = b[1].created_at ? new Date(b[1].created_at) : new Date();
                        return dateA - dateB;
                      });
                      
                      // Count locked opportunities (everything except first process)
                      const lockedCount = sortedGroups.slice(1).reduce((sum, [, group]) => sum + group.opps.length, 0);
                      return lockedCount > 0 ? `${lockedCount} additional opportunities` : 'Additional opportunities';
                    })()}
                  </span> available on Partner Pro
                </p>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="ml-auto text-sm text-[#00E5C0] hover:text-[#00D4B0] font-medium transition-colors"
                >
                  Compare plans →
                </button>
              </motion.div>
            )}
            
            <AnimatePresence mode="popLayout">
              {(() => {
                // For Basic users, determine which opportunities should be visible
                // Rule: Full access to FIRST (oldest) process/blueprint, lock all others
                if (isBasicPlan) {
                  // Group opportunities by process_id (blueprint_generator) or scan_id (assessments)
                  const processGroups = {};
                  filteredOpportunities.forEach(opp => {
                    const groupKey = opp.process_id || opp.scan_id || 'default';
                    if (!processGroups[groupKey]) {
                      processGroups[groupKey] = { opps: [], created_at: opp.created_at };
                    }
                    processGroups[groupKey].opps.push(opp);
                    // Track earliest created_at for the group
                    if (opp.created_at && (!processGroups[groupKey].created_at || new Date(opp.created_at) < new Date(processGroups[groupKey].created_at))) {
                      processGroups[groupKey].created_at = opp.created_at;
                    }
                  });

                  // Sort groups by created_at (oldest first) and get the first one
                  const sortedGroups = Object.entries(processGroups).sort((a, b) => {
                    const dateA = a[1].created_at ? new Date(a[1].created_at) : new Date();
                    const dateB = b[1].created_at ? new Date(b[1].created_at) : new Date();
                    return dateA - dateB;
                  });
                  
                  // The oldest process gets FULL access (all its opportunities are visible)
                  const oldestProcessKey = sortedGroups.length > 0 ? sortedGroups[0][0] : null;
                  
                  // Build set of visible opportunity IDs (all from oldest process)
                  const visibleOpportunityIds = new Set();
                  if (oldestProcessKey && processGroups[oldestProcessKey]) {
                    processGroups[oldestProcessKey].opps.forEach(opp => {
                      visibleOpportunityIds.add(opp.id || opp.opportunity_id);
                    });
                  }

                  return filteredOpportunities.map((opportunity, index) => {
                    const oppId = opportunity.id || opportunity.opportunity_id;
                    const isVisible = visibleOpportunityIds.has(oppId);

                    if (!isVisible) {
                      // Locked opportunity - compact preview card
                      const getEffortLabel = (effort) => {
                        if (effort <= 3) return 'Low';
                        if (effort <= 6) return 'Medium';
                        return 'High';
                      };
                      const getROILabel = (roi) => {
                        if (!roi || roi < 100) return 'Moderate ROI';
                        if (roi < 300) return 'Good ROI';
                        return 'High ROI';
                      };

                      return (
                        <motion.div
                          key={`opp-${oppId}-${selectedCategory}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ delay: index * 0.05 }}
                          className="glass rounded-2xl p-5 cursor-pointer hover:bg-[rgba(255,255,255,0.03)] transition-colors border border-white/5"
                          onClick={() => setShowUpgradeModal(true)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            {/* Left: Title and Description */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-lg font-semibold text-white truncate">
                                  {opportunity.title || `Opportunity #${index + 1}`}
                                </h3>
                                <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-[#00E5C0]/20 text-[#00E5C0] border border-[#00E5C0]/30">
                                  <Lock size={10} />
                                  Pro
                                </span>
                              </div>
                              <p className="text-sm text-[#718096] line-clamp-1 mb-2">
                                {opportunity.description || 'Full analysis available on Partner Pro'}
                              </p>
                              <p className="text-xs text-[#00E5C0]">Full analysis available on Partner Pro</p>
                            </div>
                            
                            {/* Right: Metrics */}
                            <div className="flex items-center gap-4 flex-shrink-0">
                              <div className="text-center">
                                <p className="text-xs text-[#718096] mb-1">Impact</p>
                                <p className="text-lg font-bold text-white">{opportunity.impact || '—'}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-[#718096] mb-1">Effort</p>
                                <p className="text-sm font-medium text-[#A0AEC0]">{getEffortLabel(opportunity.effort)}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-[#718096] mb-1">ROI</p>
                                <p className="text-sm font-medium text-[#14F195]">{getROILabel(opportunity.roi)}</p>
                              </div>
                              <button
                                className="flex items-center gap-1 text-sm text-[#00E5C0] hover:text-[#00D4B0] font-medium transition-colors whitespace-nowrap"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowUpgradeModal(true);
                                }}
                              >
                                Unlock →
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    }

                    // Visible opportunity - regular card
                    return (
                      <OpportunityCard
                        key={`opp-${oppId}-${selectedCategory}`}
                        id={oppId}
                        title={opportunity.title}
                        description={opportunity.description}
                        impact={opportunity.impact}
                        effort={opportunity.effort}
                        priority_score={opportunity.priority_score}
                        roi={opportunity.roi}
                        monthly_savings={opportunity.cost_savings_monthly || opportunity.monthly_savings}
                        annual_savings={opportunity.cost_savings_annual || opportunity.annual_savings}
                        time_saved={opportunity.hours_saved_monthly || opportunity.time_saved}
                        status={opportunity.status}
                        category={opportunity.category}
                        source="opportunities"
                        linked_agent_id={opportunity.linked_agent_id}
                        linked_agent_name={opportunity.linked_agent_name}
                        is_favorite={opportunity.is_favorite || false}
                        iao={opportunity.iao}
                        expected_outcomes={opportunity.expected_outcomes}
                        required_capabilities={opportunity.required_capabilities || []}
                        optional_capabilities={opportunity.optional_capabilities || []}
                        integration_notes={opportunity.integration_notes}
                        match_type={opportunity.match_type}
                        suggested_system={opportunity.suggested_system}
                        suggested_system_id={opportunity.suggested_system_id}
                        onViewDetails={() => navigate(`/opportunities/${oppId}`)}
                        onToggleFavorite={handleToggleFavorite}
                        index={index}
                        isHighlighted={oppId === highlightOpportunityId}
                        autoExpand={oppId === highlightOpportunityId}
                      />
                    );
                  });
                }

                // Pro/Partner Pro users - show all opportunities
                return filteredOpportunities.map((opportunity, index) => {
                  const oppId = opportunity.id || opportunity.opportunity_id;
                  return (
                    <OpportunityCard
                      key={`opp-${oppId}-${selectedCategory}`}
                      id={oppId}
                      title={opportunity.title}
                      description={opportunity.description}
                      impact={opportunity.impact}
                      effort={opportunity.effort}
                      priority_score={opportunity.priority_score}
                      roi={opportunity.roi}
                      monthly_savings={opportunity.cost_savings_monthly || opportunity.monthly_savings}
                      annual_savings={opportunity.cost_savings_annual || opportunity.annual_savings}
                      time_saved={opportunity.hours_saved_monthly || opportunity.time_saved}
                      status={opportunity.status}
                      category={opportunity.category}
                      source="opportunities"
                      linked_agent_id={opportunity.linked_agent_id}
                      linked_agent_name={opportunity.linked_agent_name}
                      is_favorite={opportunity.is_favorite || false}
                      iao={opportunity.iao}
                      expected_outcomes={opportunity.expected_outcomes}
                      required_capabilities={opportunity.required_capabilities || []}
                      optional_capabilities={opportunity.optional_capabilities || []}
                      integration_notes={opportunity.integration_notes}
                      match_type={opportunity.match_type}
                      suggested_system={opportunity.suggested_system}
                      suggested_system_id={opportunity.suggested_system_id}
                      onViewDetails={() => navigate(`/opportunities/${oppId}`)}
                      onToggleFavorite={handleToggleFavorite}
                      index={index}
                      isHighlighted={oppId === highlightOpportunityId}
                      autoExpand={oppId === highlightOpportunityId}
                    />
                  );
                });
              })()}
            </AnimatePresence>
          </div>

          {/* Empty State — only shown when filters are active and return nothing */}
          {filteredOpportunities.length === 0 && !loading && (searchQuery || selectedCategory !== 'all' || selectedPriority !== 'all' || selectedStatus !== 'all') && (
            <div className="glass rounded-2xl p-12 text-center">
              <Target size={64} className="mx-auto mb-6 text-[#A0AEC0]" />
              <h2 className="text-2xl font-bold text-white mb-4">No Opportunities Found</h2>
              <p className="text-[#A0AEC0] mb-8">
                Try adjusting your filters or search query
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedPriority('all');
                  setSelectedStatus('all');
                }}
                className="px-6 py-3 rounded-full bg-[#00E5C0] text-[#0B0F14] font-semibold hover:bg-[#00CCB0] transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Agent Creation Modal */}
        {showAgentModal && selectedOpportunity && (
          <AgentPreviewModal
            isOpen={showAgentModal}
            onClose={() => {
              setShowAgentModal(false);
              setSelectedOpportunity(null);
            }}
            roleData={{
              title: selectedOpportunity.recommended_agent_config?.role_title || selectedOpportunity.title,
              department: selectedOpportunity.category
            }}
            opportunityData={selectedOpportunity}
            organizationId={activeOrganization?.organization_id}
            onCreateAgent={async (agentData) => {
              // Handle agent creation
              console.log('Creating agent:', agentData);
              setShowAgentModal(false);
              setSelectedOpportunity(null);
              fetchOpportunities(); // Refresh to show linked agents
            }}
          />
        )}

        {/* Upgrade Modal */}
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentTier={userPlan || 'basic'}
          message="Unlock All Opportunities"
          feature="Full AI Opportunity Analysis"
          showComparison={true}
        />
      </>
  );
};

export default OpportunitiesDashboard;
